<template>
  <v-card
    id="screenContent"
    class="secondary vetalfa-view"
    :max-width="_getMaxWidth()"
    flat
  >
    <div class="vetalfa-global-header secondary" ref="screenHeader">
      <vetalfa-small-header
        v-if="!_isMobile()"
        showBreadCrumbAndTitle
        title="Conversas"
        breadcrumb="Conversas"
      ></vetalfa-small-header>
      <!-- Mobile header -->
      <v-row v-if="_isMobile()" no-gutters align="center" class="secondary conv-mobile-header-row px-4 pb-2">
        <v-btn icon small @click="routeBack()">
          <v-icon color="primary">{{ mdiChevronLeft }}</v-icon>
        </v-btn>
        <div class="conv-mobile-header-icon ml-2">
          <v-icon color="primary" size="16">{{ mdiCreation }}</v-icon>
        </div>
        <span class="subtitle-1 font-weight-bold primary--text ml-2">Conversas</span>
      </v-row>
    </div>
    <div
      ref="screenContent"
      style="width: 100%"
      @touchstart="onPullStart"
      @touchmove="onPullMove"
      @touchend="onPullEnd"
    >
      <!-- Pull to refresh indicator -->
      <div
        class="conv-pull-indicator"
        :style="{ height: pullDistance + 'px', opacity: pullDistance > 10 ? 1 : 0 }"
      >
        <v-progress-circular
          v-if="refreshing"
          indeterminate
          size="24"
          width="2"
          color="primary"
        ></v-progress-circular>
        <v-icon
          v-else
          :style="{ transform: pullDistance > 60 ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }"
          color="grey"
          size="24"
        >{{ mdiChevronDown }}</v-icon>
      </div>
      <v-row no-gutters justify="center" class="secondary pa-4">
        <v-col cols="12" class="ma-0 pa-0" style="max-width: 1000px">
          <!-- Skeleton loader -->
          <div v-if="loading && !conversations.length">
            <div v-for="n in 5" :key="'skel-' + n" class="conv-skeleton-card">
              <div class="conv-skeleton-bar conv-skeleton-title"></div>
              <div class="conv-skeleton-bar conv-skeleton-preview"></div>
              <div class="conv-skeleton-bar conv-skeleton-meta"></div>
            </div>
          </div>

          <!-- Empty state -->
          <div
            v-if="!loading && !conversations.length"
            class="conv-empty-state"
          >
            <div class="conv-empty-avatar">
              <v-icon color="primary" size="40">{{ mdiCreation }}</v-icon>
            </div>
            <div class="text-h6 font-weight-bold primary--text mt-4">
              Vet Alfa Intelligence
            </div>
            <div class="subtitle-2 grey--text text--darken-1 mt-1 text-center" style="max-width: 280px">
              Tire suas duvidas sobre medicina veterinaria
            </div>
            <v-btn
              color="primary"
              class="text-none mt-6 conv-new-btn"
              rounded
              @click="startNewConversation"
            >
              <v-icon left small>{{ mdiPlus }}</v-icon>
              Nova Conversa
            </v-btn>
          </div>

          <!-- Grouped conversation cards -->
          <template v-if="!loading || conversations.length">
            <div v-for="group in groupedConversations" :key="group.label">
              <div class="conv-group-label caption grey--text text--darken-1 font-weight-bold text-uppercase mb-2 mt-4 px-1">
                {{ group.label }}
              </div>
              <div
                v-for="item in group.items"
                :key="item.conversationId"
                class="conv-swipe-wrapper"
                @touchstart="onSwipeStart($event, item.conversationId)"
                @touchmove="onSwipeMove($event, item.conversationId)"
                @touchend="onSwipeEnd($event, item.conversationId)"
              >
                <div
                  class="conv-swipe-action"
                  :style="{ opacity: getSwipeOffset(item.conversationId) > 40 ? 1 : 0 }"
                >
                  <v-icon color="white" size="20">{{ mdiDeleteOutline }}</v-icon>
                </div>
                <v-card
                  class="conv-card vetalfa-pointer"
                  flat
                  :style="{ transform: 'translateX(' + (-getSwipeOffset(item.conversationId)) + 'px)' }"
                  @click="onCardClick(item)"
                >
                  <v-row no-gutters align="center">
                    <v-col class="grow ma-0 pa-0" style="min-width: 0">
                      <div class="conv-card-title subtitle-2 font-weight-bold primary--text">
                        {{ item.title || 'Conversa sem titulo' }}
                      </div>
                      <div
                        v-if="item.lastMessage"
                        class="caption grey--text conv-card-preview mt-1"
                      >
                        {{ item.lastMessage }}
                      </div>
                      <div class="d-flex align-center mt-1">
                        <span
                          v-if="item.status === 'processing'"
                          class="conv-pulse-dot conv-pulse-dot--orange mr-2"
                        ></span>
                        <span
                          v-else-if="item.status === 'error'"
                          class="conv-pulse-dot conv-pulse-dot--red mr-2"
                        ></span>
                        <v-icon x-small class="grey--text text--darken-1 mr-1">{{ mdiClockOutline }}</v-icon>
                        <span class="caption grey--text text--darken-1">
                          {{ formatDate(item.updatedAt) }}
                        </span>
                        <v-icon x-small class="grey--text text--darken-1 ml-3 mr-1">{{ mdiMessageOutline }}</v-icon>
                        <span class="caption grey--text text--darken-1">
                          {{ item.messageCount }}
                        </span>
                      </div>
                    </v-col>
                    <v-col class="shrink ma-0 pa-0">
                      <v-icon small class="grey--text">{{ mdiChevronRight }}</v-icon>
                    </v-col>
                  </v-row>
                </v-card>
              </div>
            </div>
          </template>

          <!-- Load more -->
          <v-row
            v-if="conversationsNextCursor"
            no-gutters
            justify="center"
            class="mt-4"
          >
            <v-btn
              text
              small
              color="primary"
              class="text-none conv-load-more"
              :loading="loadingMore"
              @click="loadMore"
            >
              Carregar mais
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-row no-gutters class="transparent" style="height: 200px"></v-row>
    </div>

    <!-- FAB -->
    <v-btn
      v-if="conversations.length"
      fab
      color="primary"
      class="conv-fab"
      @click="startNewConversation"
    >
      <v-icon color="white">{{ mdiPlus }}</v-icon>
    </v-btn>
  </v-card>
</template>

<style scoped>
/* Pull to refresh */
.conv-pull-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: opacity 0.2s ease;
}

/* Mobile header */
.conv-mobile-header-row {
  padding-top: calc(12px + env(safe-area-inset-top));
  padding-top: calc(12px + constant(safe-area-inset-top));
}
.conv-mobile-header-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(25, 49, 83, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Empty state */
.conv-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 64px 16px 32px;
}
.conv-empty-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(25, 49, 83, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}
.conv-new-btn {
  padding: 0 24px !important;
  letter-spacing: 0.02em;
}

/* Group label */
.conv-group-label {
  letter-spacing: 0.06em;
}

/* Skeleton */
.conv-skeleton-card {
  margin-bottom: 12px;
  padding: 14px 16px;
  border: 1px solid #eeeeee;
  border-left: 3px solid #e0e0e0;
  border-radius: 10px;
  background: white;
}
.conv-skeleton-bar {
  border-radius: 4px;
  background: linear-gradient(90deg, #eee 25%, #f5f5f5 50%, #eee 75%);
  background-size: 200% 100%;
  animation: conv-shimmer 1.5s infinite;
}
.conv-skeleton-title {
  width: 65%;
  height: 14px;
  margin-bottom: 8px;
}
.conv-skeleton-preview {
  width: 90%;
  height: 11px;
  margin-bottom: 8px;
}
.conv-skeleton-meta {
  width: 40%;
  height: 10px;
}
@keyframes conv-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Swipe */
.conv-swipe-wrapper {
  position: relative;
  overflow: hidden;
  margin-bottom: 12px;
  border-radius: 10px;
}
.conv-swipe-action {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 80px;
  background: #e53935;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 10px 10px 0;
  transition: opacity 0.15s ease;
}

/* Cards */
.conv-card {
  padding: 14px 16px;
  border: 1px solid #eeeeee !important;
  border-left: 3px solid #193153 !important;
  border-radius: 10px !important;
  background: white !important;
  transition: box-shadow 0.2s ease, transform 0.15s ease;
  position: relative;
  z-index: 1;
}
.conv-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
}
.conv-card:active {
  transform: scale(0.99);
}
.conv-card-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.conv-card-preview {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
}

/* Pulse dots */
.conv-pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
.conv-pulse-dot--orange {
  background-color: #fb8c00;
  animation: conv-pulse 1.5s infinite ease-in-out;
}
.conv-pulse-dot--red {
  background-color: #e53935;
}
@keyframes conv-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

/* Fade-in transition */
.conv-fade-enter-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}
.conv-fade-enter {
  opacity: 0;
  transform: translateY(8px);
}

/* Load more link */
.conv-load-more {
  font-weight: 500;
  letter-spacing: 0.01em;
}

/* FAB */
.conv-fab {
  position: fixed !important;
  bottom: 24px;
  right: 24px;
  z-index: 100;
}
</style>

<script>
import Vue from "vue";
import { mapGetters, mapActions } from "vuex";
import {
  mdiPlus, mdiChevronRight, mdiChevronLeft, mdiChevronDown,
  mdiClockOutline, mdiMessageOutline, mdiCreation, mdiDeleteOutline
} from "@mdi/js";

export default {
  name: "AIConversations",
  data() {
    return {
      loading: false,
      loadingMore: false,
      refreshing: false,
      mdiPlus,
      mdiChevronRight,
      mdiChevronLeft,
      mdiChevronDown,
      mdiClockOutline,
      mdiMessageOutline,
      mdiCreation,
      mdiDeleteOutline,
      pollingInterval: null,
      // Swipe state
      swipeStates: {},
      swipeStartX: 0,
      swipeStartY: 0,
      swipingId: null,
      // Pull to refresh
      pullStartY: 0,
      pullDistance: 0,
      isPulling: false,
    };
  },
  computed: {
    ...mapGetters("topics", ["conversations", "conversationsNextCursor"]),
    hasProcessing() {
      return this.conversations.some((c) => c.status === "processing");
    },
    groupedConversations() {
      if (!this.conversations || !this.conversations.length) return [];
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      const yesterdayStart = todayStart - 86400000;
      const weekStart = todayStart - (now.getDay() * 86400000);

      const groups = {
        today: { label: "Hoje", items: [] },
        yesterday: { label: "Ontem", items: [] },
        week: { label: "Esta semana", items: [] },
        older: { label: "Anteriores", items: [] },
      };

      for (const conv of this.conversations) {
        const t = conv.updatedAt || conv.createdAt || 0;
        if (t >= todayStart) {
          groups.today.items.push(conv);
        } else if (t >= yesterdayStart) {
          groups.yesterday.items.push(conv);
        } else if (t >= weekStart) {
          groups.week.items.push(conv);
        } else {
          groups.older.items.push(conv);
        }
      }

      return Object.values(groups).filter((g) => g.items.length > 0);
    },
  },
  async mounted() {
    this.loading = true;
    await this.loadConversations();
    this.loading = false;
    this.setupScreenSizes();
    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    ...mapActions("topics", ["loadConversations", "loadMoreConversations", "deleteConversation"]),
    ...mapActions("general", ["routePathRedirect", "routeBack"]),
    startNewConversation() {
      this.routePathRedirect({ path: "/ai-conversations/new" });
    },
    openConversation(item) {
      this.routePathRedirect({
        path: `/ai-conversations/${item.conversationId}`,
      });
    },
    onCardClick(item) {
      if (this.getSwipeOffset(item.conversationId) > 10) return;
      this.openConversation(item);
    },
    async loadMore() {
      this.loadingMore = true;
      await this.loadMoreConversations();
      this.loadingMore = false;
    },
    formatDate(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMin = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMin < 1) return "agora";
      if (diffMin < 60) return `${diffMin}min`;
      if (diffHours < 24) return `${diffHours}h`;
      if (diffDays < 7) return `${diffDays}d`;
      return date.toLocaleDateString("pt-BR");
    },

    // Swipe to delete
    getSwipeOffset(id) {
      return (this.swipeStates[id] && this.swipeStates[id].offset) || 0;
    },
    onSwipeStart(e, id) {
      const touch = e.touches[0];
      this.swipeStartX = touch.clientX;
      this.swipeStartY = touch.clientY;
      this.swipingId = id;
    },
    onSwipeMove(e, id) {
      if (this.swipingId !== id) return;
      const touch = e.touches[0];
      const dx = this.swipeStartX - touch.clientX;
      const dy = Math.abs(touch.clientY - this.swipeStartY);
      if (dx > 0 && dx > dy * 1.5) {
        e.preventDefault();
        const offset = Math.min(dx, 80);
        Vue.set(this.swipeStates, id, { offset });
      }
    },
    onSwipeEnd(e, id) {
      if (this.swipingId !== id) return;
      const offset = this.getSwipeOffset(id);
      if (offset > 40) {
        this.confirmDelete(id);
      }
      Vue.set(this.swipeStates, id, { offset: 0 });
      this.swipingId = null;
    },
    confirmDelete(id) {
      this.$store.commit("general/setConfirmModel", {
        text: "Deseja excluir esta conversa?",
        confirmText: "Excluir",
        alert: true,
        callback: () => {
          this.deleteConversation({ id });
        },
      });
    },

    // Pull to refresh
    onPullStart(e) {
      if (window.scrollY <= 0) {
        this.pullStartY = e.touches[0].clientY;
        this.isPulling = true;
      }
    },
    onPullMove(e) {
      if (!this.isPulling || this.refreshing) return;
      if (window.scrollY > 0) {
        this.pullDistance = 0;
        return;
      }
      const dy = e.touches[0].clientY - this.pullStartY;
      if (dy > 0) {
        this.pullDistance = Math.round(dy * 0.4);
      }
    },
    async onPullEnd() {
      if (!this.isPulling) return;
      this.isPulling = false;
      if (this.pullDistance > 60) {
        this.refreshing = true;
        await this.loadConversations();
        this.refreshing = false;
      }
      this.pullDistance = 0;
    },

    startPolling() {
      this.pollingInterval = setInterval(async () => {
        if (this.hasProcessing) {
          await this.loadConversations();
        }
      }, 5000);
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    setupScreenSizes() {
      this._setupScreenSizes(this);
    },
  },
};
</script>
